/**
 * 59. Cycle Counts — count sheets; entry; variance; approve.
 *
 * Arabic and English (C7): every word from the catalogs; quantities are
 * numbers the locale writes, statuses agree with the count.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatQuantity, formatDate } from "@/lib/money";

interface CycleCountRow {
  id: string;
  countNumber: string;
  warehouseName: string;
  productSku: string;
  systemQuantity: string;
  countedQuantity: string;
  variance: string;
  countedBy: string;
  countedAt: string | null;
  status: "Draft" | "InProgress" | "Reviewed" | "Approved" | "Posted";
}

export function CycleCountsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<CycleCountRow>(
    ["inventory", "cycle-counts"],
    "/inventory/cycle-counts",
  );

  return (
    <div>
      <PageHeader
        title={t("inventory.cycleCounts.title")}
        subtitle={t("inventory.cycleCounts.subtitle")}
        actions={
          <PendingButton
            variant="primary"
            reason="stockDocuments"
            slice="CO-W2"
          >
            {t("inventory.cycleCounts.start")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("inventory.cycleCounts.list"), data.total)}>
          <DataTable<CycleCountRow>
            rows={data.items}
            columns={[
              {
                key: "countNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.countNumber}</span>
                ),
              },
              { key: "warehouseName", label: t("fields.warehouse") },
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              {
                key: "systemQuantity",
                label: t("inventory.cycleCounts.system"),
                numeric: true,
                render: (r) => formatQuantity(r.systemQuantity),
              },
              {
                key: "countedQuantity",
                label: t("inventory.cycleCounts.counted"),
                numeric: true,
                render: (r) => formatQuantity(r.countedQuantity),
              },
              {
                key: "variance",
                label: t("inventory.cycleCounts.variance"),
                numeric: true,
                render: (r) => (
                  <Badge
                    tone={
                      Number(r.variance) === 0
                        ? "success"
                        : Math.abs(Number(r.variance)) > 0.0001
                          ? "warning"
                          : "default"
                    }
                  >
                    {formatQuantity(r.variance)}
                  </Badge>
                ),
              },
              {
                key: "countedBy",
                label: t("inventory.cycleCounts.countedBy"),
              },
              {
                key: "countedAt",
                label: t("inventory.cycleCounts.counted"),
                render: (r) => (r.countedAt ? formatDate(r.countedAt) : "—"),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="CycleCount" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
